import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Trash2, Search, Calendar, Settings, Info, CheckCircle, XCircle, Clock, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { getSelectionLabel } from '../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminPredictionHistory = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Predictions state
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, total_items: 0 });
  
  // Filters
  const [predictionType, setPredictionType] = useState('all');
  const [outcome, setOutcome] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  
  // Settings state
  const [settings, setSettings] = useState({
    history_start_date: '',
    history_end_date: '',
    info_text: '',
    show_info_card: true
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  
  // Delete state
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    if (user?.is_admin) {
      fetchSettings();
    }
  }, [user]);

  useEffect(() => {
    if (user?.is_admin) {
      fetchPredictions();
    }
  }, [user, predictionType, outcome, startDate, endDate, currentPage]);

  const fetchPredictions = async () => {
    if (!user?.is_admin) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const params = {
        page: currentPage,
        limit: 20
      };
      if (predictionType !== 'all') params.prediction_type = predictionType;
      if (outcome !== 'all') params.outcome = outcome;
      if (startDate) params.start_date = new Date(startDate).toISOString();
      if (endDate) params.end_date = new Date(endDate).toISOString();
      
      const response = await axios.get(`${API}/api/admin/predictions-history`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPredictions(response.data.predictions);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching predictions:', error);
      if (error.response?.status !== 403) {
        toast.error('Failed to load predictions');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/api/admin/prediction-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        history_start_date: response.data.history_start_date ? response.data.history_start_date.split('T')[0] : '',
        history_end_date: response.data.history_end_date ? response.data.history_end_date.split('T')[0] : '',
        info_text: response.data.info_text || '',
        show_info_card: response.data.show_info_card !== false
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const saveSettings = async () => {
    setSettingsLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      await axios.put(`${API}/api/admin/prediction-settings`, {
        history_start_date: settings.history_start_date ? new Date(settings.history_start_date).toISOString() : null,
        history_end_date: settings.history_end_date ? new Date(settings.history_end_date).toISOString() : null,
        info_text: settings.info_text,
        show_info_card: settings.show_info_card
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved successfully');
      setShowSettingsModal(false);
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  const deletePrediction = async (predictionId) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/api/admin/predictions-history/${predictionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Prediction deleted');
      fetchPredictions();
    } catch (error) {
      console.error('Error deleting prediction:', error);
      toast.error('Failed to delete prediction');
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    try {
      const token = localStorage.getItem('session_token');
      await axios.post(`${API}/api/admin/predictions-history/bulk-delete`, {
        prediction_ids: selectedIds
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Deleted ${selectedIds.length} predictions`);
      setSelectedIds([]);
      fetchPredictions();
    } catch (error) {
      console.error('Error bulk deleting:', error);
      toast.error('Failed to delete predictions');
    }
  };

  const handleSelectAll = () => {
    if (selectedIds.length === predictions.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(predictions.map(p => p.id));
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const confirmDelete = (prediction) => {
    setDeleteTarget(prediction);
    setShowDeleteConfirm(true);
  };

  const executeDelete = () => {
    if (deleteTarget) {
      deletePrediction(deleteTarget.id);
    }
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  const getOutcomeIcon = (outcome) => {
    switch (outcome) {
      case 'won': return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'lost': return <XCircle className="w-4 h-4 text-red-400" />;
      case 'void': return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getOutcomeColor = (outcome) => {
    switch (outcome) {
      case 'won': return 'bg-green-500/20 text-green-400';
      case 'lost': return 'bg-red-500/20 text-red-400';
      case 'void': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (!user?.is_admin) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-white">Admin access required</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="admin-prediction-history" className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/admin')}
                variant="outline"
                size="sm"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">AI Prediction History</h1>
                <p className="text-gray-400 text-sm">Manage and delete prediction records</p>
              </div>
            </div>
            <Button
              onClick={() => setShowSettingsModal(true)}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Settings className="w-4 h-4 mr-2" />
              Display Settings
            </Button>
          </div>

          {/* Current Period Info */}
          {(settings.history_start_date || settings.history_end_date) && (
            <Card className="glass-dark border-blue-500/30 mb-6">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-3">
                  <Info className="w-5 h-5 text-blue-400" />
                  <div>
                    <span className="text-white font-medium">Active Display Period: </span>
                    <span className="text-blue-400">
                      {settings.history_start_date ? formatDate(settings.history_start_date) : 'Beginning'} 
                      {' → '} 
                      {settings.history_end_date ? formatDate(settings.history_end_date) : 'Present'}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Filters */}
          <Card className="glass-dark border-white/10 mb-6">
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Type</label>
                  <select
                    value={predictionType}
                    onChange={(e) => { setPredictionType(e.target.value); setCurrentPage(1); }}
                    className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-white"
                  >
                    <option value="all">All Types</option>
                    <option value="single">Singles</option>
                    <option value="double">Doubles</option>
                    <option value="treble">Trebles</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">Outcome</label>
                  <select
                    value={outcome}
                    onChange={(e) => { setOutcome(e.target.value); setCurrentPage(1); }}
                    className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-white"
                  >
                    <option value="all">All Outcomes</option>
                    <option value="won">Won</option>
                    <option value="lost">Lost</option>
                    <option value="void">Void</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">From Date</label>
                  <Input
                    type="date"
                    value={startDate}
                    onChange={(e) => { setStartDate(e.target.value); setCurrentPage(1); }}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-sm text-gray-400 mb-1 block">To Date</label>
                  <Input
                    type="date"
                    value={endDate}
                    onChange={(e) => { setEndDate(e.target.value); setCurrentPage(1); }}
                    className="bg-white/5 border-white/10 text-white"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={() => { setStartDate(''); setEndDate(''); setPredictionType('all'); setOutcome('all'); setCurrentPage(1); }}
                    variant="outline"
                    className="w-full border-white/20 text-white hover:bg-white/10"
                  >
                    Clear Filters
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bulk Actions */}
          {selectedIds.length > 0 && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-500/30 rounded-lg flex items-center justify-between">
              <span className="text-white">{selectedIds.length} prediction(s) selected</span>
              <Button
                onClick={bulkDelete}
                variant="destructive"
                size="sm"
                className="bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Selected
              </Button>
            </div>
          )}

          {/* Predictions Table */}
          <Card className="glass-dark border-white/10">
            <CardContent className="pt-6">
              {loading ? (
                <div className="text-center py-10">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                  <p className="text-gray-400 mt-2">Loading predictions...</p>
                </div>
              ) : predictions.length === 0 ? (
                <div className="text-center py-10">
                  <p className="text-gray-400">No predictions found matching your filters</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left p-3">
                            <input
                              type="checkbox"
                              checked={selectedIds.length === predictions.length && predictions.length > 0}
                              onChange={handleSelectAll}
                              className="rounded"
                            />
                          </th>
                          <th className="text-left p-3 text-gray-400 text-sm">Date</th>
                          <th className="text-left p-3 text-gray-400 text-sm">Type</th>
                          <th className="text-left p-3 text-gray-400 text-sm">Matches</th>
                          <th className="text-left p-3 text-gray-400 text-sm">Odds</th>
                          <th className="text-left p-3 text-gray-400 text-sm">Outcome</th>
                          <th className="text-left p-3 text-gray-400 text-sm">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {predictions.map((pred) => (
                          <tr key={pred.id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="p-3">
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(pred.id)}
                                onChange={() => handleSelectOne(pred.id)}
                                className="rounded"
                              />
                            </td>
                            <td className="p-3 text-white text-sm">{formatDate(pred.date)}</td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                pred.prediction_type === 'single' ? 'bg-blue-500/20 text-blue-400' :
                                pred.prediction_type === 'double' ? 'bg-purple-500/20 text-purple-400' :
                                'bg-orange-500/20 text-orange-400'
                              }`}>
                                {pred.prediction_type?.charAt(0).toUpperCase() + pred.prediction_type?.slice(1)}
                              </span>
                            </td>
                            <td className="p-3 text-sm">
                              <div className="max-w-xs">
                                {pred.matches?.slice(0, 2).map((m, i) => (
                                  <div key={i} className="text-gray-300 truncate">
                                    <span>{m.home_team} vs {m.away_team}</span>
                                    {m.selection && (
                                      <span className="text-blue-400 ml-1 text-xs">
                                        ({getSelectionLabel(m.selection, m.home_team, m.away_team)} @{m.odds})
                                      </span>
                                    )}
                                  </div>
                                ))}
                                {pred.matches?.length > 2 && (
                                  <span className="text-gray-500 text-xs">+{pred.matches.length - 2} more</span>
                                )}
                              </div>
                            </td>
                            <td className="p-3 text-white font-medium">
                              {pred.adjusted_total_odds ? (
                                <span className="text-yellow-400">{pred.adjusted_total_odds?.toFixed(2)}</span>
                              ) : (
                                pred.total_odds?.toFixed(2)
                              )}
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                {getOutcomeIcon(pred.outcome)}
                                <span className={`px-2 py-1 rounded text-xs font-medium ${getOutcomeColor(pred.outcome)}`}>
                                  {pred.outcome ? pred.outcome.toUpperCase() : 'PENDING'}
                                </span>
                              </div>
                            </td>
                            <td className="p-3">
                              <Button
                                onClick={() => confirmDelete(pred)}
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
                    <div className="text-sm text-gray-400">
                      Showing {((pagination.current_page - 1) * 20) + 1} - {Math.min(pagination.current_page * 20, pagination.total_items)} of {pagination.total_items}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        variant="outline"
                        size="sm"
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        Previous
                      </Button>
                      <span className="px-3 py-1 text-white">
                        {pagination.current_page} / {pagination.total_pages}
                      </span>
                      <Button
                        onClick={() => setCurrentPage(p => Math.min(pagination.total_pages, p + 1))}
                        disabled={currentPage === pagination.total_pages}
                        variant="outline"
                        size="sm"
                        className="border-white/20 text-white hover:bg-white/10"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Settings className="w-5 h-5 text-blue-400" />
              Prediction Display Settings
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-gray-400 mb-1 block">History Start Date</label>
                <Input
                  type="date"
                  value={settings.history_start_date}
                  onChange={(e) => setSettings({...settings, history_start_date: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for no start limit</p>
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1 block">History End Date</label>
                <Input
                  type="date"
                  value={settings.history_end_date}
                  onChange={(e) => setSettings({...settings, history_end_date: e.target.value})}
                  className="bg-white/5 border-white/10 text-white"
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty for no end limit</p>
              </div>
            </div>
            <div>
              <label className="text-sm text-gray-400 mb-1 block">Info Card Text</label>
              <textarea
                value={settings.info_text}
                onChange={(e) => setSettings({...settings, info_text: e.target.value})}
                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-white h-24 resize-none"
                placeholder="Enter text to display in the info card on the predictions page..."
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showInfoCard"
                checked={settings.show_info_card}
                onChange={(e) => setSettings({...settings, show_info_card: e.target.checked})}
                className="rounded"
              />
              <label htmlFor="showInfoCard" className="text-sm text-gray-300">Show info card on predictions page</label>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowSettingsModal(false)}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={saveSettings}
              disabled={settingsLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {settingsLoading ? 'Saving...' : 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="bg-slate-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Confirm Delete</DialogTitle>
          </DialogHeader>
          <p className="text-gray-300 py-4">
            Are you sure you want to delete this prediction? This will also update the statistics cards automatically.
          </p>
          {deleteTarget && (
            <div className="bg-white/5 rounded p-3 mb-4">
              <p className="text-sm text-gray-400">
                {deleteTarget.prediction_type?.toUpperCase()} - {formatDate(deleteTarget.date)}
              </p>
              {deleteTarget.matches?.map((m, i) => (
                <p key={i} className="text-white text-sm">{m.home_team} vs {m.away_team}</p>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={() => setShowDeleteConfirm(false)}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={executeDelete}
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default AdminPredictionHistory;
